import React, { useState, useRef, useEffect } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import { TRANSLATIONS } from '../../translations';

const BetaTextInput = (props) => {
  const [shouldLabelFloat, setShouldLabelFloat] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [charCount, setCharCount] = useState(
    (props.defaultValue && props.defaultValue.length) || 0
  );
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (inputRef.current?.value) {
      setShouldLabelFloat(true);
    }
  }, [inputRef.current?.value]);

  const handleContainerFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    setIsFocused(true);
  };

  const handleContainerBlur = (e) => {
    if (!e.target.value) {
      setShouldLabelFloat(false);
    }
    setIsFocused(false);
  };

  const updateCharCount = debounce((value) => {
    setCharCount(value.length);
  }, 300);

  const handleTextAreaInput = (e) => {
    const { value } = e.target;
    if (props.setError) {
      if (value.length > 0) {
        props.setError(null);
      } else {
        props.setError(props.errorMessage);
      }
    }
    updateCharCount(value);
    if (props.onChange) {
      props.onChange(e);
    }
  };

  return (
    <>
      <div
        className={`beta-text-input ${
          props.resize ? 'beta-text-input--resizable' : ''
        } ${props.error ? 'beta-text-input--error' : ''} `}
        ref={containerRef}
        tabIndex={0}
        onFocus={handleContainerFocus}
      >
        <p
          className={`text-left beta-text-input__label${
            isFocused || shouldLabelFloat ? '--focused' : ''
          } ${props.error ? 'error' : ''}`}
        >
          {props.label}
        </p>

        {props.asTextArea ? (
          <textarea
            rows={props.rows}
            id={props.name}
            name={props.name}
            ref={(ref) => {
              inputRef.current = ref;
              props.setRef(ref);
            }}
            onBlur={handleContainerBlur}
            onFocus={() => setShouldLabelFloat(true)}
            onInput={handleTextAreaInput}
            data-gtm-item={props.dataGtmItem}
            data-gtm-detail={props.dataGtmDetail}
            defaultValue={props.defaultValue}
            maxLength={props.limit}
          />
        ) : (
          <input
            type="text"
            id={props.name}
            name={props.name}
            ref={(ref) => {
              inputRef.current = ref;
              props.setRef(ref);
            }}
            onBlur={handleContainerBlur}
            onFocus={() => setShouldLabelFloat(true)}
            onInput={handleTextAreaInput}
            data-gtm-item={props.dataGtmItem}
            data-gtm-detail={props.dataGtmDetail}
            defaultValue={props.defaultValue}
            maxLength={props.limit}
          />
        )}
        {props.limit && (
          <p className="beta-text-input__footer">
            {charCount}/{props.limit}
          </p>
        )}
      </div>
      {props.error && (
        <p className="typography__error">
          <small className="error-message">
            <b>{props.error}</b>
          </small>
        </p>
      )}
    </>
  );
};

BetaTextInput.propTypes = {
  setRef: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  shadowed: PropTypes.bool,
  errored: PropTypes.bool,
  resize: PropTypes.bool,
  className: PropTypes.string,
  asTextArea: PropTypes.bool,
  type: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  rows: PropTypes.number,
  max: PropTypes.number,
  dataGtmItem: PropTypes.string,
  dataGtmDetail: PropTypes.string,
  defaultValue: PropTypes.string,
  limit: PropTypes.number,
  error: PropTypes.string,
  setError: PropTypes.func,
  errorMessage: PropTypes.string,
};

BetaTextInput.defaultProps = {
  onChange: null,
  readOnly: false,
  disabled: false,
  shadowed: false,
  errored: false,
  resize: false,
  asTextArea: false,
  max: null,
  type: 'text',
  defaultValue: '',
  limit: 900,
  error: null,
  setError: null,
  errorMessage: TRANSLATIONS.UNEXPECTED_ERROR,
};

export { BetaTextInput };
